import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaClipboardList,
  FaGift,
  FaHome,
  FaSearch,
  FaSun,
  FaSyncAlt,
  FaTimesCircle,
  FaUserClock,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import SelectClass from "../ChildComponent/SelectClass";
import boyImage from "../img/boy.jpg";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function getCurrentYearMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

//Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

//Created By Cursor — months from session start → end
function buildSessionMonths(startRaw, endRaw) {
  const session = SessionManager.getCurrentSession()?.[0];
  const start =
    parseLooseDate(startRaw) || parseLooseDate(session?.start_date);
  const end = parseLooseDate(endRaw) || parseLooseDate(session?.end_date);
  if (!start || !end) return [getCurrentYearMonth()];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (cursor > last) return [getCurrentYearMonth()];
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months.length ? months : [getCurrentYearMonth()];
}

//Created By Cursor
function pickDefaultMonth(months) {
  const current = getCurrentYearMonth();
  if (!months?.length) return current;
  if (months.includes(current)) return current;
  if (current < months[0]) return months[0];
  return months[months.length - 1];
}

//Created By Cursor
function formatMonthLabel(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

//Created By Cursor
function formatMonthShort(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "short" });
}

//Created By Cursor
function monthYear(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  return m ? m[1] : "";
}

//Created By Cursor
function splitYearMonth(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) {
    const current = getCurrentYearMonth().split("-");
    return { year: current[0], month: current[1] };
  }
  return { year: m[1], month: m[2] };
}

//Created By Cursor
function KpiChip({ tone, icon: Icon, label, value }) {
  return (
    <div className={`sm-kpi-chip sm-kpi-chip--${tone}`}>
      <span className="sm-kpi-chip__icon" aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <div className="sm-kpi-chip__text">
        <span className="sm-kpi-chip__label">{label}</span>
        <span className="sm-kpi-chip__value">{value}</span>
      </div>
    </div>
  );
}

//Created By Cursor
function detailPath(student, classId, sectionId, className, sectionName, month) {
  const noSection = sectionId === "No" || !sectionId;
  const secId = noSection ? "No" : sectionId;
  const secName = noSection ? "null" : sectionName || "null";
  const path = `/AdminMain/StudentAttendanceDetail/${classId}/${secId}/${encodeURIComponent(
    student.student_enrollment_no || ""
  )}/${encodeURIComponent(className || "")}/${encodeURIComponent(
    secName
  )}/${encodeURIComponent(student.stu_name || "Student")}`;
  return month ? `${path}?month=${encodeURIComponent(month)}` : path;
}

//Created By Cursor
function studentInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase();
}

//Created By Cursor
export default function StudentAttendanceList() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionStart, setSessionStart] = useState("");
  const [sessionEnd, setSessionEnd] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);
  const [selectedMonth, setSelectedMonth] = useState(() =>
    pickDefaultMonth(buildSessionMonths())
  );
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [monthStats, setMonthStats] = useState({
    month_days: 0,
    weekends: 0,
    holidays: 0,
    school_days: 0,
  });

  const classLabel = sectionName
    ? `${className}:${sectionName}`
    : className || "Select a class";

  const monthOptions = useMemo(
    () => buildSessionMonths(sessionStart, sessionEnd),
    [sessionStart, sessionEnd]
  );
  const currentYearMonth = useMemo(() => getCurrentYearMonth(), []);
  const selectedMonthParts = useMemo(
    () => splitYearMonth(selectedMonth),
    [selectedMonth]
  );
  const selectedMonthLabel = useMemo(
    () => formatMonthLabel(selectedMonth),
    [selectedMonth]
  );

  const totals = useMemo(() => {
    const present = students.reduce((n, s) => n + Number(s.P || 0), 0);
    const absent = students.reduce((n, s) => n + Number(s.A || 0), 0);
    const leave = students.reduce((n, s) => n + Number(s.L || 0), 0);
    const taken = students.reduce((n, s) => n + Number(s.taken_days || 0), 0);
    const avgPct =
      taken === 0 ? 0 : Math.floor((present * 100) / taken);
    return { present, absent, leave, count: students.length, avgPct };
  }, [students]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = String(s.stu_name || "").toLowerCase();
      const father = String(s.father_name || "").toLowerCase();
      const roll = String(s.student_roll_no || "").toLowerCase();
      const enroll = String(s.student_enrollment_no || "").toLowerCase();
      return (
        name.includes(q) ||
        father.includes(q) ||
        roll.includes(q) ||
        enroll.includes(q)
      );
    });
  }, [students, search]);

  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position,
      class_name,
      section_name,
      _session_name,
      start_date,
      end_date
    ) => {
      setClassId(class_id);
      setClassName(class_name || "");
      setSectionName(section_name || "");
      setSectionId(section_id);
      setSessionId(session_id);
      setSessionStart(start_date || "");
      setSessionEnd(end_date || "");
      setSelectedClassPosition(selected_position);
      setStudents([]);
      setSearch("");
      setMonthStats({
        month_days: 0,
        weekends: 0,
        holidays: 0,
        school_days: 0,
      });
    },
    []
  );

  const handleMonthChange = useCallback((value) => {
    setSelectedMonth(value);
  }, []);

  const loadStudents = useCallback(async () => {
    if (!classId || !sectionId || !sessionId) return;
    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = {
        school_code: schoolCode,
        class_id: classId,
        section_id: sectionId,
        session_id: sessionId,
        month: selectedMonthParts.month,
        year: selectedMonthParts.year,
      };
      const url = `${API_BASE()}getClassStudentsAttendanceMonthStats.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);
      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        setStudents(Array.isArray(response.data) ? response.data : []);
        const ms = response.month_stats || {};
        setMonthStats({
          month_days: Number(ms.month_days || 0),
          weekends: Number(ms.weekends || 0),
          holidays: Number(ms.holidays || 0),
          school_days: Number(ms.school_days || 0),
        });
      } else {
        setStudents([]);
        setMonthStats({
          month_days: 0,
          weekends: 0,
          holidays: 0,
          school_days: 0,
        });
        myToast.notify_warning(
          response?.api_remark || "No students found for this class."
        );
      }
    } catch (error) {
      console.error(error);
      setStudents([]);
      setMonthStats({
        month_days: 0,
        weekends: 0,
        holidays: 0,
        school_days: 0,
      });
      myToast.notify_warning("Failed to load student attendance.");
    } finally {
      setLoading(false);
    }
  }, [
    classId,
    sectionId,
    sessionId,
    selectedMonthParts.month,
    selectedMonthParts.year,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!monthOptions.length) return;
    setSelectedMonth((prev) =>
      monthOptions.includes(prev) ? prev : pickDefaultMonth(monthOptions)
    );
  }, [sessionStart, sessionEnd, monthOptions]);

  useEffect(() => {
    if (selectedClassPosition >= 0 && classId && sectionId && sessionId) {
      loadStudents();
    }
  }, [selectedClassPosition, classId, sectionId, sessionId, loadStudents]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page tat-page sat-page"
      style={{ fontFamily: PAGE_FONT, background: "#ffffff" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Attendance</span>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Attendance Student</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sat-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Attendance Student</h1>
              <p className="sm-hero__sub">
                {classLabel} · {selectedMonthLabel}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/MonthAttendance" className="tat-hero-link">
              <FaCalendarAlt aria-hidden />
              <span>Monthly View</span>
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadStudents}
              disabled={loading || !classId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="sat-body">
          <div className="sat-filters">
            <SelectClass
              action={callBackClassSelect}
              selected_position={selectedClassPosition}
            />
          </div>

          <section className="sat-month-panel" aria-label="Select month">
            <div className="sat-month-panel__head">
              <h2>
                <FaCalendarAlt aria-hidden /> Attendance month
              </h2>
              <span className="sat-month-panel__badge">
                {selectedMonthLabel}
              </span>
            </div>
            <div className="sat-month-track">
              <div
                className="sat-month-grid"
                role="group"
                aria-label="Session months"
                style={{
                  "--sat-month-count": Math.max(monthOptions.length, 1),
                }}
              >
                {monthOptions.map((item) => {
                  const active = item === selectedMonth;
                  const isCurrent = item === currentYearMonth;
                  return (
                    <button
                      key={item}
                      type="button"
                      className={`sat-month-chip${active ? " is-active" : ""}${
                        isCurrent && !active ? " is-current" : ""
                      }`}
                      onClick={() => handleMonthChange(item)}
                      aria-pressed={active}
                      title={formatMonthLabel(item)}
                    >
                      <span className="sat-month-chip__name">
                        {formatMonthShort(item)}
                      </span>
                      <span className="sat-month-chip__year">
                        {isCurrent ? "Now" : monthYear(item)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {!classId ? (
            <div className="sat-empty-card">
              <span className="sat-empty-card__icon" aria-hidden>
                <FaUsers />
              </span>
              <h2>Choose a class</h2>
              <p>
                Select class and section to see each student’s attendance for{" "}
                <strong>{selectedMonthLabel}</strong>.
              </p>
            </div>
          ) : (
            <>
              <div className="sat-meta-card">
                <div className="sm-kpi-row sat-meta-chips" aria-label="Class month summary">
                  <KpiChip
                    tone="blue"
                    icon={FaCalendarAlt}
                    label="Month"
                    value={selectedMonthLabel}
                  />
                  <KpiChip
                    tone="sky"
                    icon={FaCalendarAlt}
                    label="Month Day"
                    value={monthStats.month_days}
                  />
                  <KpiChip
                    tone="sand"
                    icon={FaSun}
                    label="Weekend"
                    value={monthStats.weekends}
                  />
                  <KpiChip
                    tone="sand"
                    icon={FaGift}
                    label="Holidays"
                    value={monthStats.holidays}
                  />
                  <KpiChip
                    tone="mint"
                    icon={FaClipboardList}
                    label="School Day"
                    value={monthStats.school_days}
                  />
                  <KpiChip
                    tone="mint"
                    icon={FaCheckCircle}
                    label="Class average"
                    value={`${totals.avgPct}% present`}
                  />
                </div>
              </div>

              <div className="sm-kpi-row sat-kpis" aria-label="Month totals">
                <KpiChip
                  tone="sky"
                  icon={FaUsers}
                  label="Students"
                  value={totals.count}
                />
                <KpiChip
                  tone="mint"
                  icon={FaCheckCircle}
                  label="Present"
                  value={totals.present}
                />
                <KpiChip
                  tone="sand"
                  icon={FaTimesCircle}
                  label="Absent"
                  value={totals.absent}
                />
                <KpiChip
                  tone="blue"
                  icon={FaUserClock}
                  label="Leave"
                  value={totals.leave}
                />
              </div>

              {loading && !students.length ? (
                <div className="sat-loading">
                  <MyLoader />
                </div>
              ) : students.length === 0 ? (
                <div className="sat-empty-card">
                  <span className="sat-empty-card__icon" aria-hidden>
                    <FaClipboardList />
                  </span>
                  <h2>No students found</h2>
                  <p>
                    This class has no active students for {selectedMonthLabel}.
                  </p>
                </div>
              ) : (
                <section className="sat-list-panel">
                  <div className="sat-list-panel__head">
                    <div className="min-w-0">
                      <h2 className="sat-list-panel__title">Student list</h2>
                      <p className="sat-list-panel__hint">
                        {selectedMonthLabel} · tap a student for day-wise
                        details
                      </p>
                    </div>
                    <div className="sat-list-panel__tools">
                      <label className="sat-search">
                        <FaSearch aria-hidden />
                        <input
                          type="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search name, roll…"
                          aria-label="Search students"
                        />
                      </label>
                      <span className="sat-list-panel__count">
                        {filteredStudents.length}
                        {search.trim() ? ` / ${students.length}` : ""}
                      </span>
                    </div>
                  </div>

                  <div className="sat-list-cols" aria-hidden>
                    <span>Student</span>
                    <span>Month stats</span>
                  </div>

                  {filteredStudents.length === 0 ? (
                    <div className="sat-empty sat-empty--soft">
                      No students match “{search.trim()}”.
                    </div>
                  ) : (
                    <div className="sat-student-list" role="list">
                      {filteredStudents.map((student, index) => {
                        const name = student.stu_name || "—";
                        const present = Number(student.P || 0);
                        const absent = Number(student.A || 0);
                        const leave = Number(student.L || 0);
                        const taken = Number(student.taken_days || 0);
                        const pct =
                          taken === 0
                            ? 0
                            : Math.floor((present * 100) / taken);
                        const initials = studentInitials(name);
                        return (
                          <Link
                            key={
                              student.student_enrollment_no ||
                              `${name}-${index}`
                            }
                            role="listitem"
                            className="sat-student-row"
                            to={detailPath(
                              student,
                              classId,
                              sectionId,
                              className,
                              sectionName,
                              selectedMonth
                            )}
                          >
                            <span className="sat-student-row__roll">
                              {student.student_roll_no || index + 1}
                            </span>
                            <span className="sat-student-row__avatar">
                              <img
                                src={student.student_img_path || boyImage}
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback =
                                    e.currentTarget.nextElementSibling;
                                  if (fallback)
                                    fallback.style.display = "inline-flex";
                                }}
                              />
                              <span
                                className="sat-student-row__initials"
                                style={{ display: "none" }}
                                aria-hidden
                              >
                                {initials || "?"}
                              </span>
                            </span>
                            <span className="sat-student-row__body">
                              <strong>{name}</strong>
                              <small>
                                S/O {student.father_name || "—"}
                                {student.student_enrollment_no
                                  ? ` · ${student.student_enrollment_no}`
                                  : ""}
                              </small>
                              <span
                                className="sat-student-row__bar"
                                aria-hidden
                              >
                                <i style={{ width: `${Math.min(100, pct)}%` }} />
                              </span>
                            </span>
                            <span
                              className="sat-student-row__stats"
                              aria-label="Month stats"
                            >
                              <span className="is-p" title="Present">
                                <span className="sat-stat-tick" aria-hidden>
                                  ✓
                                </span>
                                <em>P</em>
                                <b>{present}</b>
                              </span>
                              <span className="is-a" title="Absent">
                                <span className="sat-stat-tick" aria-hidden>
                                  ✓
                                </span>
                                <em>A</em>
                                <b>{absent}</b>
                              </span>
                              <span className="is-l" title="Leave">
                                <span className="sat-stat-tick" aria-hidden>
                                  ✓
                                </span>
                                <em>L</em>
                                <b>{leave}</b>
                              </span>
                              <span className="is-pct" title="Present %">
                                {pct}%
                              </span>
                            </span>
                            <span className="sat-student-row__go" aria-hidden>
                              <FaChevronRight />
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
